// DiscoverFeed.js
// Social learning platform - TikTok-style feed for study content
// Students can share, like, comment, and save educational content

import React, { useState, useEffect } from 'react';
import {
  X, Heart, MessageCircle, Bookmark, Share2, TrendingUp, Clock,
  Filter, Search, ChevronDown, Check, Flag, Plus, Sparkles,
  BookOpen, FileText, Award, Users, Eye, Star, ExternalLink, Edit2
} from 'lucide-react';
import UploadContentModal from './UploadContentModal';

const DiscoverFeed = ({ darkMode, currentUser, supabase, makeOpenAIRequest, onClose, onAddToStudyPlan }) => {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('all'); // all, notes, quizzes, summaries, flashcards
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [sortBy, setSortBy] = useState('trending'); // trending, recent, top_rated
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingContent, setEditingContent] = useState(null);

  const subjects = ['All', 'Math', 'Science', 'English', 'History', 'SAT', 'ACT', 'NEET', 'JEE', 'Physics', 'Chemistry', 'Biology'];
  const contentTypes = [
    { value: 'all', label: 'All Content', icon: '📚' },
    { value: 'notes', label: 'Notes', icon: '📝' },
    { value: 'summary', label: 'Summaries', icon: '📄' },
    { value: 'quiz', label: 'Quizzes', icon: '❓' },
    { value: 'flashcards', label: 'Flashcards', icon: '🗂️' },
    { value: 'video_link', label: 'Video Links', icon: '🎥' },
    { value: 'study_guide', label: 'Study Guides', icon: '📖' }
  ];

  useEffect(() => {
    loadDiscoverContent();
  }, [selectedFilter, selectedSubject, sortBy, searchQuery]);

  const loadDiscoverContent = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('discover_content')
        .select(`
          *
        `)
        .eq('is_published', true)
        .eq('verification_status', 'approved');

      // Apply filters
      if (selectedFilter !== 'all') {
        query = query.eq('content_type', selectedFilter);
      }

      if (selectedSubject !== 'all') {
        query = query.eq('subject', selectedSubject.toLowerCase());
      }

      if (searchQuery) {
        query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
      }

      // For now, get all data and sort client-side
      query = query.limit(100);

      const { data, error } = await query;

      if (error) throw error;

      // Calculate engagement score for each item
      const calculateEngagementScore = (item) => {
        const views = item.views_count || 0;
        const likes = item.likes_count || 0;
        const saves = item.saves_count || 0;
        const comments = item.comments_count || 0;

        // Weighted score: views * 1 + likes * 5 + saves * 10 + comments * 3
        return (views * 1) + (likes * 5) + (saves * 10) + (comments * 3);
      };

      // Sort based on selected criteria
      let sortedData = [...data];
      if (sortBy === 'trending') {
        sortedData.sort((a, b) => calculateEngagementScore(b) - calculateEngagementScore(a));
      } else if (sortBy === 'recent') {
        sortedData.sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
      } else if (sortBy === 'top_rated') {
        sortedData.sort((a, b) => (b.likes_count || 0) - (a.likes_count || 0));
      }

      // Limit to 50 items after sorting
      sortedData = sortedData.slice(0, 50);

      // Check if current user has liked or saved each content and get user info
      const enrichedData = await Promise.all(sortedData.map(async (item) => {
        const { data: likeData } = await supabase
          .from('discover_likes')
          .select('id')
          .eq('content_id', item.id)
          .eq('user_id', currentUser.id)
          .maybeSingle();

        const { data: saveData } = await supabase
          .from('discover_saves')
          .select('id')
          .eq('content_id', item.id)
          .eq('user_id', currentUser.id)
          .maybeSingle();

        // Get user info from content metadata (stored when uploaded)
        let userData = null;

        // First, check if this is the current user
        if (item.user_id === currentUser.id) {
          userData = {
            id: currentUser.id,
            email: currentUser.email,
            raw_user_meta_data: currentUser.user_metadata
          };
        } else {
          // Get creator info from content metadata
          const creatorName = item.content_data?.metadata?.creatorName;
          const creatorEmail = item.content_data?.metadata?.creatorEmail;

          userData = {
            id: item.user_id,
            email: creatorEmail || 'unknown@example.com',
            raw_user_meta_data: { name: creatorName || 'Anonymous' }
          };
        }

        return {
          ...item,
          user: userData,
          isLiked: !!likeData,
          isSaved: !!saveData
        };
      }));

      setContent(enrichedData);
    } catch (error) {
      console.error('Error loading discover content:', error);
    }
    setLoading(false);
  };

  const handleLike = async (contentId, isCurrentlyLiked) => {
    try {
      if (isCurrentlyLiked) {
        // Unlike
        await supabase
          .from('discover_likes')
          .delete()
          .eq('content_id', contentId)
          .eq('user_id', currentUser.id);
      } else {
        // Like
        await supabase
          .from('discover_likes')
          .insert({
            content_id: contentId,
            user_id: currentUser.id
          });
      }

      // Refresh content
      loadDiscoverContent();
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleSave = async (contentId, isCurrentlySaved) => {
    try {
      if (isCurrentlySaved) {
        // Unsave
        await supabase
          .from('discover_saves')
          .delete()
          .eq('content_id', contentId)
          .eq('user_id', currentUser.id);
      } else {
        // Save
        await supabase
          .from('discover_saves')
          .insert({
            content_id: contentId,
            user_id: currentUser.id
          });
      }

      // Refresh content
      loadDiscoverContent();
    } catch (error) {
      console.error('Error toggling save:', error);
    }
  };

  const trackView = async (contentId) => {
    try {
      await supabase
        .from('discover_views')
        .insert({
          content_id: contentId,
          user_id: currentUser.id
        });

      // Update view count
      await supabase.rpc('increment', {
        table_name: 'discover_content',
        row_id: contentId,
        column_name: 'views_count'
      });
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  };

  const getContentIcon = (type) => {
    const typeObj = contentTypes.find(t => t.value === type);
    return typeObj ? typeObj.icon : '📚';
  };

  const getDifficultyColor = (level) => {
    if (!level) return 'bg-gray-500';
    switch (level) {
      case 'beginner': return 'bg-green-500';
      case 'intermediate': return 'bg-yellow-500';
      case 'advanced': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const handleEdit = (content) => {
    setEditingContent(content);
    setShowUploadModal(true);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={`w-full max-w-7xl h-[95vh] rounded-3xl shadow-2xl overflow-hidden ${
        darkMode ? 'bg-gray-900' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b flex items-center justify-between ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gradient-to-r from-purple-50 to-pink-50 border-gray-200'
        }`}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Discover
              </h2>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Learn from your peers
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:scale-105 transition-transform"
            >
              <Plus className="w-5 h-5" />
              <span>Upload</span>
            </button>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
              }`}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className={`px-6 py-4 border-b ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center gap-4 mb-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for notes, quizzes, topics..."
                className={`w-full pl-10 pr-4 py-2 rounded-xl border ${
                  darkMode
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                } focus:outline-none focus:ring-2 focus:ring-purple-500`}
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              } hover:scale-105 transition-transform`}
            >
              <Filter className="w-5 h-5" />
              <span>Filters</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="space-y-4 animate-fadeIn">
              {/* Content Type Filter */}
              <div>
                <label className={`text-sm font-semibold mb-2 block ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Content Type
                </label>
                <div className="flex flex-wrap gap-2">
                  {contentTypes.map(type => (
                    <button
                      key={type.value}
                      onClick={() => setSelectedFilter(type.value)}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                        selectedFilter === type.value
                          ? 'bg-purple-500 border-purple-500 text-white'
                          : darkMode
                            ? 'bg-gray-700 border-gray-600 text-gray-300 hover:border-purple-500'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-purple-400'
                      }`}
                    >
                      <span className="mr-2">{type.icon}</span>
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Subject Filter */}
              <div>
                <label className={`text-sm font-semibold mb-2 block ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Subject
                </label>
                <div className="flex flex-wrap gap-2">
                  {subjects.map(subject => (
                    <button
                      key={subject}
                      onClick={() => setSelectedSubject(subject.toLowerCase())}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                        selectedSubject === subject.toLowerCase()
                          ? 'bg-purple-500 border-purple-500 text-white'
                          : darkMode
                            ? 'bg-gray-700 border-gray-600 text-gray-300 hover:border-purple-500'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-purple-400'
                      }`}
                    >
                      {subject}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sort Options */}
              <div>
                <label className={`text-sm font-semibold mb-2 block ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Sort By
                </label>
                <div className="flex gap-2">
                  {[
                    { value: 'trending', label: 'Trending', icon: TrendingUp },
                    { value: 'recent', label: 'Most Recent', icon: Clock },
                    { value: 'top_rated', label: 'Top Rated', icon: Star }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => setSortBy(option.value)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                        sortBy === option.value
                          ? 'bg-purple-500 border-purple-500 text-white'
                          : darkMode
                            ? 'bg-gray-700 border-gray-600 text-gray-300 hover:border-purple-500'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-purple-400'
                      }`}
                    >
                      <option.icon className="w-4 h-4" />
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Content Feed */}
        <div className={`h-[calc(100%-200px)] overflow-y-auto p-6 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Loading amazing content...</p>
              </div>
            </div>
          ) : content.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <BookOpen className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                <h3 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  No content found
                </h3>
                <p className={`mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Be the first to share your knowledge!
                </p>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:scale-105 transition-transform"
                >
                  Upload Content
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {content.map(item => (
                <ContentCard
                  key={item.id}
                  item={item}
                  darkMode={darkMode}
                  currentUser={currentUser}
                  onLike={() => handleLike(item.id, item.isLiked)}
                  onSave={() => handleSave(item.id, item.isSaved)}
                  onView={() => {
                    setSelectedContent(item);
                    trackView(item.id);
                  }}
                  onEdit={handleEdit}
                  onAddToStudyPlan={onAddToStudyPlan}
                  getContentIcon={getContentIcon}
                  getDifficultyColor={getDifficultyColor}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadContentModal
          darkMode={darkMode}
          currentUser={currentUser}
          supabase={supabase}
          makeOpenAIRequest={makeOpenAIRequest}
          editingContent={editingContent}
          onClose={() => {
            setShowUploadModal(false);
            setEditingContent(null);
          }}
          onSuccess={() => {
            setShowUploadModal(false);
            setEditingContent(null);
            loadDiscoverContent();
          }}
        />
      )}

      {/* Content Detail Modal */}
      {selectedContent && (
        <ContentDetailModal
          content={selectedContent}
          darkMode={darkMode}
          currentUser={currentUser}
          supabase={supabase}
          onClose={() => setSelectedContent(null)}
          onLike={() => handleLike(selectedContent.id, selectedContent.isLiked)}
          onSave={() => handleSave(selectedContent.id, selectedContent.isSaved)}
          onAddToStudyPlan={onAddToStudyPlan}
        />
      )}
    </div>
  );
};

// Content Card Component
const ContentCard = ({ item, darkMode, currentUser, onLike, onSave, onView, onEdit, onAddToStudyPlan, getContentIcon, getDifficultyColor }) => {
  const isOwner = item.user_id === currentUser?.id;

  return (
    <div
      className={`rounded-2xl border p-5 transition-all hover:scale-105 ${
        darkMode
          ? 'bg-gray-800 border-gray-700 hover:border-purple-500'
          : 'bg-white border-gray-200 hover:border-purple-400 hover:shadow-xl'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={onView}>
          <div className="text-3xl">{getContentIcon(item.content_type)}</div>
          <div>
            <h3 className={`font-bold text-lg line-clamp-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {item.title}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              {item.ai_verified && (
                <div className="flex items-center gap-1 text-xs text-green-500">
                  <Check className="w-3 h-3" />
                  <span>AI Verified</span>
                </div>
              )}
              <span className={`text-xs px-2 py-1 rounded ${getDifficultyColor(item.difficulty_level)} text-white`}>
                {item.difficulty_level || 'All Levels'}
              </span>
            </div>
          </div>
        </div>
        {isOwner && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(item);
            }}
            className={`p-2 rounded-lg transition-colors ${
              darkMode
                ? 'text-gray-400 hover:text-purple-400 hover:bg-gray-700'
                : 'text-gray-500 hover:text-purple-500 hover:bg-purple-50'
            }`}
            title="Edit your content"
          >
            <Edit2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Description */}
      <p className={`text-sm mb-4 line-clamp-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        {item.description}
      </p>

      {/* Tags */}
      {item.tags && item.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {item.tags.slice(0, 3).map((tag, idx) => (
            <span
              key={idx}
              className={`text-xs px-2 py-1 rounded ${
                darkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'
              }`}
            >
              #{tag}
            </span>
          ))}
          {item.tags.length > 3 && (
            <span className={`text-xs px-2 py-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              +{item.tags.length - 3} more
            </span>
          )}
        </div>
      )}

      {/* Stats and Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-700">
        <div className="flex items-center gap-4 text-sm">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onLike();
            }}
            className={`flex items-center gap-1 transition-colors ${
              item.isLiked ? 'text-red-500' : darkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-500 hover:text-red-500'
            }`}
          >
            <Heart className={`w-4 h-4 ${item.isLiked ? 'fill-current' : ''}`} />
            <span>{item.likes_count}</span>
          </button>

          <div className={`flex items-center gap-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <MessageCircle className="w-4 h-4" />
            <span>{item.comments_count}</span>
          </div>

          <div className={`flex items-center gap-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <Eye className="w-4 h-4" />
            <span>{item.views_count}</span>
          </div>
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onSave();
          }}
          className={`p-2 rounded-lg transition-colors ${
            item.isSaved
              ? 'text-purple-500'
              : darkMode
                ? 'text-gray-400 hover:text-purple-400 hover:bg-gray-700'
                : 'text-gray-500 hover:text-purple-500 hover:bg-purple-50'
          }`}
        >
          <Bookmark className={`w-5 h-5 ${item.isSaved ? 'fill-current' : ''}`} />
        </button>
      </div>

      {/* Creator Info */}
      <div className={`mt-4 pt-4 border-t flex items-center gap-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white text-sm font-bold">
          {item.user?.raw_user_meta_data?.name?.[0] || item.user?.email?.[0] || 'U'}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {item.user?.raw_user_meta_data?.name || item.user?.email?.split('@')[0] || 'Anonymous'}
          </p>
          <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            {item.estimated_time_minutes ? `${item.estimated_time_minutes} min read` : 'Quick read'}
          </p>
        </div>
      </div>
    </div>
  );
};

// Content Detail Modal Component
const ContentDetailModal = ({ content, darkMode, currentUser, supabase, onClose, onLike, onSave, onAddToStudyPlan }) => {
  const getContentIcon = (type) => {
    const icons = {
      notes: '📝',
      summary: '📄',
      quiz: '❓',
      flashcards: '🗂️',
      video_link: '🎥',
      study_guide: '📖'
    };
    return icons[type] || '📚';
  };

  const getDifficultyColor = (level) => {
    if (!level) return 'bg-gray-500';
    switch (level) {
      case 'beginner': return 'bg-green-500';
      case 'intermediate': return 'bg-yellow-500';
      case 'advanced': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const renderContent = () => {
    const contentData = content.content_data;

    if (!contentData) {
      return <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>No content available</p>;
    }

    // Handle different content types
    if (content.content_type === 'video_link' && contentData.url) {
      return (
        <div className="space-y-4">
          <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <p className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Video URL:</p>
            <a
              href={contentData.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-500 hover:underline break-all"
            >
              {contentData.url}
            </a>
          </div>
          {contentData.text && (
            <div className={`prose max-w-none ${darkMode ? 'prose-invert' : ''}`}>
              <div className="whitespace-pre-wrap">{contentData.text}</div>
            </div>
          )}
        </div>
      );
    }

    // Handle quiz content
    if (content.content_type === 'quiz' && contentData.questions) {
      return (
        <div className="space-y-6">
          {contentData.questions.map((q, idx) => (
            <div key={idx} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <p className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {idx + 1}. {q.question}
              </p>
              <div className="space-y-2 ml-4">
                {q.options?.map((option, optIdx) => (
                  <div
                    key={optIdx}
                    className={`p-2 rounded ${
                      optIdx === q.correctAnswer
                        ? darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700'
                        : darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    {String.fromCharCode(65 + optIdx)}. {option}
                    {optIdx === q.correctAnswer && ' ✓'}
                  </div>
                ))}
              </div>
              {q.explanation && (
                <p className={`mt-3 text-sm italic ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Explanation: {q.explanation}
                </p>
              )}
            </div>
          ))}
        </div>
      );
    }

    // Handle flashcards
    if (content.content_type === 'flashcards' && contentData.cards) {
      return (
        <div className="space-y-4">
          {contentData.cards.map((card, idx) => (
            <div key={idx} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <p className={`font-semibold mb-2 ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                Question {idx + 1}:
              </p>
              <p className={`mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{card.front}</p>
              <p className={`text-sm font-semibold mb-1 ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                Answer:
              </p>
              <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{card.back}</p>
            </div>
          ))}
        </div>
      );
    }

    // Handle text content (notes, summaries, study guides)
    if (contentData.text) {
      // The content is already AI-formatted with proper spacing, just display it nicely
      return (
        <div className={`prose prose-lg max-w-none ${darkMode ? 'prose-invert' : ''}`}>
          <div className={`whitespace-pre-wrap leading-relaxed text-base ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {contentData.text}
          </div>
        </div>
      );
    }

    return <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>No content available</p>;
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`w-full max-w-5xl rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} max-h-[90vh] overflow-hidden flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="text-4xl">{getContentIcon(content.content_type)}</div>
              <div className="flex-1">
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {content.title}
                </h2>
                <div className="flex items-center gap-2 mt-2">
                  {content.ai_verified && (
                    <div className="flex items-center gap-1 text-xs text-green-500">
                      <Check className="w-3 h-3" />
                      <span>AI Verified</span>
                    </div>
                  )}
                  <span className={`text-xs px-2 py-1 rounded ${getDifficultyColor(content.difficulty_level)} text-white`}>
                    {content.difficulty_level || 'All Levels'}
                  </span>
                  {content.subject && (
                    <span className={`text-xs px-2 py-1 rounded ${darkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                      {content.subject}
                    </span>
                  )}
                  {content.estimated_time_minutes && (
                    <span className={`text-xs flex items-center gap-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <Clock className="w-3 h-3" />
                      {content.estimated_time_minutes} min
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
              }`}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Description */}
          {content.description && (
            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {content.description}
            </p>
          )}

          {/* Tags */}
          {content.tags && content.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {content.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className={`text-xs px-2 py-1 rounded ${
                    darkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'
                  }`}
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {renderContent()}
        </div>

        {/* Footer Actions */}
        <div className={`p-4 border-t ${darkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onLike();
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  content.isLiked
                    ? 'bg-red-500 text-white'
                    : darkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <Heart className={`w-5 h-5 ${content.isLiked ? 'fill-current' : ''}`} />
                <span>{content.likes_count || 0}</span>
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onSave();
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  content.isSaved
                    ? 'bg-purple-500 text-white'
                    : darkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <Bookmark className={`w-5 h-5 ${content.isSaved ? 'fill-current' : ''}`} />
                <span>{content.isSaved ? 'Saved' : 'Save'}</span>
              </button>

              <div className={`flex items-center gap-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <Eye className="w-4 h-4" />
                <span>{content.views_count || 0} views</span>
              </div>
            </div>

            {onAddToStudyPlan && (
              <button
                onClick={() => onAddToStudyPlan(content)}
                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-semibold hover:scale-105 transition-transform"
              >
                Add to Study Plan
              </button>
            )}
          </div>

          {/* Creator Info */}
          <div className={`mt-4 pt-4 border-t flex items-center gap-3 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
              {content.user?.raw_user_meta_data?.name?.[0] || content.user?.email?.[0] || 'U'}
            </div>
            <div>
              <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Created by {content.user?.raw_user_meta_data?.name || content.user?.email?.split('@')[0] || 'Anonymous'}
              </p>
              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                {new Date(content.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscoverFeed;
